/**
 * 本地模組頁面配置
 *
 * 定義 core 專案內的本地模組頁面（tools, field, data），自動生成：
 * - remoteModules
 * - moduleLoaders (本地 import 路徑)
 * - pagePermissions (僅 tools，field/data 依賴案場權限)
 * - sideNavMenus
 */

import type { LocalModuleConfig, PageConfig } from './types';

// ============================================================================
// Tools 模組配置
// ============================================================================

export const toolsPages: PageConfig[] = [
    {
        name: 'UserUIList',
        path: '@/views/tools/UserUIList.vue',
        title: '工具列表',
        mode: 'tools',
        icon: 'icofont-numbered',
        default: true,
        showInSideNav: true,
    },
    {
        name: 'UserUIRenderer',
        path: '@/views/tools/UserUIRenderer.vue',
        title: '工具渲染',
        mode: 'tools',
        default: true,
        // 不需要獨立權限，由 UserUIList 進入
    },
];

export const toolsConfig: LocalModuleConfig = {
    name: 'tools',
    basePath: '@/views/tools',
    pages: toolsPages,
};

// ============================================================================
// Field 模組配置（本地案場頁面，依賴案場權限）
// ============================================================================

export const fieldPages: PageConfig[] = [
    {
        name: 'FieldDetail',
        path: '@/views/field/FieldDetail.vue',
        title: '案場詳情',
        mode: 'field',
        default: true,
    },
    {
        name: 'FieldStatistics',
        path: '@/views/field/FieldStatistics.vue',
        title: '案場統計',
        mode: 'field',
        default: true,
    },
    {
        name: 'FieldData',
        path: '@/views/field/FieldData.vue',
        title: '案場數據',
        mode: 'field',
        default: true,
    },
    {
        name: 'FieldEquipments',
        path: '@/views/field/FieldEquipments.vue',
        title: '案場設備',
        mode: 'field',
        default: true,
    },
    {
        name: 'FieldSystem',
        path: '@/views/field/FieldSystem.vue',
        title: '案場系統',
        mode: 'field',
        default: true,
    },
    {
        name: 'FieldContracts',
        path: '@/views/field/FieldContracts.vue',
        title: '案場合約',
        mode: 'field',
        default: true,
    },
    {
        name: 'FieldDrawings',
        path: '@/views/field/FieldDrawings.vue',
        title: '案場圖說',
        mode: 'field',
        default: true,
    },
    {
        name: 'FieldFiles',
        path: '@/views/field/FieldFiles.vue',
        title: '案場檔案',
        mode: 'field',
        default: true,
    },
    {
        name: 'FieldMember',
        path: '@/views/field/FieldMember.vue',
        title: '案場成員',
        mode: 'field',
        default: true,
    },
    {
        name: 'FieldResume',
        path: '@/views/field/FieldResume.vue',
        title: '案場履歷',
        mode: 'field',
        default: true,
    },
    {
        name: 'FieldWorkOrderList',
        path: '@/views/field/FieldWorkOrderList.vue',
        title: '案場工單',
        mode: 'field',
        default: true,
    },
    {
        name: 'FieldEventsList',
        path: '@/views/field/FieldEventsList.vue',
        title: '案場事件列表',
        mode: 'field',
        default: true,
    },
    {
        name: 'FieldBillList',
        path: '@/views/field/FieldBillList.vue',
        title: '案場帳單',
        mode: 'field',
        default: true,
    },
    {
        name: 'FieldDelete',
        path: '@/views/field/FieldDelete.vue',
        title: '案場刪除',
        mode: 'field',
        default: true,
    },
    {
        name: 'FieldShared',
        path: '@/views/field/FieldShared.vue',
        title: '案場分享',
        mode: 'field',
        default: true,
    },
    {
        name: 'FieldEssStrategySettings',
        path: '@/views/field/FieldEssStrategySettings.vue',
        title: '案場策略',
        mode: 'field',
        default: true,
    },
    {
        name: 'FieldFinance',
        path: '@/views/field/FieldFinance.vue',
        title: '案場收支',
        mode: 'field',
        default: true,
    },
];

export const fieldConfig: LocalModuleConfig = {
    name: 'field',
    basePath: '@/views/field',
    pages: fieldPages,
};

// ============================================================================
// Data 模組配置（設備數據頁面，依賴案場權限）
// ============================================================================

export const dataPages: PageConfig[] = [
    {
        name: 'FieldDashboard',
        path: '@/views/data/FieldDashboard.vue',
        title: '案場數據',
        mode: 'data',
        default: true,
    },
    {
        name: 'EdpData',
        path: '@/views/data/EdpData.vue',
        title: '調度',
        mode: 'data',
        default: true,
    },
    {
        name: 'EssData',
        path: '@/views/data/EssData.vue',
        title: '儲能櫃',
        mode: 'data',
        default: true,
    },
    {
        name: 'InverterData',
        path: '@/views/data/InverterData.vue',
        title: '逆變器',
        mode: 'data',
        default: true,
    },
    {
        name: 'GenData',
        path: '@/views/data/GenData.vue',
        title: '發電機',
        mode: 'data',
        default: true,
    },
    {
        name: 'PowerMeterData',
        path: '@/views/data/PowerMeterData.vue',
        title: '電錶',
        mode: 'data',
        default: true,
    },
    {
        name: 'TempData',
        path: '@/views/data/TempData.vue',
        title: '溫度',
        mode: 'data',
        default: true,
    },
    {
        name: 'PyranometerData',
        path: '@/views/data/PyranometerData.vue',
        title: '日照',
        mode: 'data',
        default: true,
    },
    {
        name: 'PressureData',
        path: '@/views/data/PressureData.vue',
        title: '壓力',
        mode: 'data',
        default: true,
    },
    {
        name: 'FlowData',
        path: '@/views/data/FlowData.vue',
        title: '流量',
        mode: 'data',
        default: true,
    },
    {
        name: 'IPCData',
        path: '@/views/data/IPCData.vue',
        title: '電腦',
        mode: 'data',
        default: true,
    },
    {
        name: 'OtherData',
        path: '@/views/data/OtherData.vue',
        title: '其它',
        mode: 'data',
        default: true,
    },
];

export const dataConfig: LocalModuleConfig = {
    name: 'data',
    basePath: '@/views/data',
    pages: dataPages,
};

// ============================================================================
// Admin 模組配置（權限管理頁面，原在 management Remote）
// ============================================================================

export const adminPages: PageConfig[] = [
    {
        name: 'PagePermissionByOrg',
        path: '@/views/admin/PagePermissionByOrg.vue',
        title: '組織權限設定',
        mode: 'management',
        icon: 'icofont-people',
        default: false,
        showInSideNav: true,
    },
    {
        name: 'PagePermissionByPage',
        path: '@/views/admin/PagePermissionByPage.vue',
        title: '頁面權限設定',
        mode: 'management',
        icon: 'icofont-lock',
        default: false,
        showInSideNav: true,
    },
];

export const adminConfig: LocalModuleConfig = {
    name: 'admin',
    basePath: '@/views/admin',
    pages: adminPages,
};

// ============================================================================
// Report 模組配置（收支明細頁面，從 om Remote 遷移至 core 以支援 hot-reload）
// ============================================================================

export const reportPages: PageConfig[] = [
    {
        name: 'MaintenanceFinanceList',
        path: '@/views/report/MaintenanceFinanceList.vue',
        title: '收支明細',
        mode: 'report',
        icon: 'icofont-money',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '報表', order: 5 },
    },
    {
        name: 'MaintenanceFinanceDetail',
        path: '@/views/report/MaintenanceFinanceDetail.vue',
        title: '收支明細詳情',
        mode: 'report',
        default: true,
    },
];

export const reportConfig: LocalModuleConfig = {
    name: 'report',
    basePath: '@/views/report',
    pages: reportPages,
};

// ============================================================================
// Inventory 模組配置（備品管理頁面）
// ============================================================================

export const inventoryPages: PageConfig[] = [
    {
        name: 'SparePartsList',
        path: '@/views/inventory/SparePartsList.vue',
        title: '備品管理',
        mode: 'operation',
        icon: 'icofont-box',
        default: true,
        showInSideNav: true,
    },
];

export const inventoryConfig: LocalModuleConfig = {
    name: 'inventory',
    basePath: '@/views/inventory',
    pages: inventoryPages,
};

// ============================================================================
// 匯出所有本地模組配置
// ============================================================================

export const localModules = {
    tools: toolsConfig,
    field: fieldConfig,
    data: dataConfig,
    admin: adminConfig,
    report: reportConfig,
    inventory: inventoryConfig,
};

export default localModules;
